import React, { useEffect, useState } from "react";
import { api, API, formatDistance, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Camera, FileSignature, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, MinusCircle, Download, ExternalLink } from "lucide-react";
import { SupplementalEvidenceList } from "@/components/SupplementalEvidence";

const STATE_META = {
  verified: { label: "Verified", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  present: { label: "Present", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  required_missing: { label: "Required — Missing", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  missing: { label: "Missing", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  unavailable_review: { label: "Unavailable — Review Required", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
  not_required: { label: "Not Required", cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: MinusCircle },
  not_applicable: { label: "Not Applicable", cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: MinusCircle },
};

function StateBadge({ state, testid }) {
  const m = STATE_META[state] || STATE_META.unavailable_review;
  const { Icon } = m;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {m.label}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

function ProximityNote({ v, testid }) {
  if (!v || !v.status) return null;
  const META = {
    pass: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck, label: "Location verified" },
    flagged: { cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertTriangle, label: "Outside service location" },
    not_validated: { cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: MinusCircle, label: "Proximity not validated" },
  };
  const m = META[v.status] || META.not_validated;
  const { Icon } = m;
  return (
    <div className="mt-2 border-t border-slate-100 pt-2" data-testid={testid}>
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
        <Icon className="h-3.5 w-3.5" /> {m.label}
      </span>
      {v.distance_m != null && <p className="mt-1 text-[11px] text-slate-500">{formatDistance(v.distance_m)} from service address (allowed {formatDistance(v.allowed_m)})</p>}
      {v.status === "not_validated" && <p className="mt-1 text-[11px] text-slate-400">{v.reason}</p>}
    </div>
  );
}

const CENSUS_RESULT_META = {
  "Verified": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  "Likely Verified": { cls: "bg-teal-50 text-teal-700 border-teal-200", Icon: CheckCircle2 },
  "Review Required": { cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
  "Not Verified": { cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  "Insufficient Data": { cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: MinusCircle },
};

function CensusVerification({ cv, attemptNo }) {
  if (!cv) return null;
  const m = CENSUS_RESULT_META[cv.result] || CENSUS_RESULT_META["Insufficient Data"];
  const { Icon } = m;
  const fg = cv.field_gps || {};
  return (
    <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`census-verification-${attemptNo}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Field GPS</p>
      <p className="font-mono-arps text-xs text-slate-700">{fg.lat != null && fg.lng != null ? `${Number(fg.lat).toFixed(5)}, ${Number(fg.lng).toFixed(5)}` : "—"}</p>
      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Nearest Physical Address (Census Bureau Geocoder)</p>
      <p className="text-xs text-slate-700" data-testid={`census-observed-${attemptNo}`}>{cv.observed_address || "Insufficient Data"}</p>
      <dl className="mt-2 space-y-0.5 text-[11px] text-slate-500">
        <div><span className="text-slate-400">Target Address:</span> {cv.target_address || "—"}</div>
        <div><span className="text-slate-400">Distance from Target:</span> {cv.distance_from_target_m != null ? formatDistance(cv.distance_from_target_m) : "—"}</div>
        <div><span className="text-slate-400">GPS Accuracy:</span> {cv.gps_accuracy_m != null ? `±${Math.round(cv.gps_accuracy_m)}m` : "—"}</div>
      </dl>
      <div className="mt-1.5">
        <span data-testid={`census-result-${attemptNo}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
          <Icon className="h-3.5 w-3.5" /> {cv.result}
        </span>
      </div>
      <p className="mt-1.5 text-[10px] leading-tight text-slate-400">{cv.disclaimer}</p>
    </div>
  );
}

function PhotoTile({ photo, idx, attemptNo }) {
  const [open, setOpen] = useState(false);
  const src = photo.inline ? photo.inline_data : (photo.view_url ? `${API}${photo.view_url.replace("/api", "")}` : null);
  // A stored file must stay openable for authorized users through every workflow status and integrity
  // flag. The StateBadge/detail below still surface any flag; access itself is enforced server-side.
  const viewable = !!src;
  return (
    <>
      <button
        data-testid={`evidence-photo-${attemptNo}-${idx}`}
        onClick={() => viewable && setOpen(true)}
        disabled={!viewable}
        className={`flex flex-col items-start gap-1 rounded-lg border p-2 text-left ${viewable ? "border-slate-200 hover:ring-2 hover:ring-emerald-400 cursor-pointer" : "border-amber-200 bg-amber-50/40 cursor-not-allowed"}`}
      >
        {viewable ? (
          <img src={src} alt="Service evidence" className="h-16 w-16 rounded object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded bg-amber-100"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
        )}
        <StateBadge state={photo.state} />
        {photo.detail && <span className="max-w-[9rem] text-[10px] leading-tight text-amber-700">{photo.detail}</span>}
      </button>
      {viewable && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Photo evidence — Attempt #{attemptNo}</DialogTitle></DialogHeader>
            <img src={src} alt="Service evidence full" className="w-full rounded-lg" />
            {photo.view_url && <a href={src} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open original file</a>}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function ClientAttemptCard({ a }) {
  const n = a.attempt_number;
  return (
    <div data-testid={`evidence-attempt-${n}`} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-display text-base font-bold text-slate-900">Attempt #{n}</h4>
        <span data-testid={`evidence-outcome-${n}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${a.outcome === "served" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
          {(a.outcome || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500" data-testid={`evidence-times-${n}`}>{fmt(a.device_timestamp) || fmt(a.timestamp) || "—"}</p>
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-700">
        <span data-testid={`evidence-gps-recorded-${n}`}>GPS recorded: <b>{a.gps_recorded ? "Yes" : "No"}</b></span>
        <span data-testid={`evidence-photo-recorded-${n}`}>Photo recorded: <b>{a.photo_recorded ? "Yes" : "No"}</b></span>
      </div>
    </div>
  );
}

export function EvidenceChain({ orderId, role }) {
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(false);
  const load = React.useCallback(() => {
    api.get(`/orders/${orderId}/evidence`).then(({ data }) => setEv(data)).catch(() => setErr(true));
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const overrideAttempt = async (a) => {
    const note = window.prompt(`Admin override — count Attempt #${a.attempt_number} toward the 4 attempts and make it payable. Enter a written reason (logged):`);
    if (note == null) return;
    if (note.trim().length < 5) { toast.error("A written override note is required."); return; }
    try {
      await api.post(`/admin/orders/${orderId}/attempts/${a.attempt_id}/verify-override`, { note: note.trim() });
      toast.success(`Attempt #${a.attempt_number} overridden — now counted and payable.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not override attempt."); }
  };

  if (err) return <p className="py-6 text-center text-sm text-red-400">Unable to load evidence.</p>;
  if (!ev) return <p className="py-6 text-center text-sm text-slate-400">Loading evidence…</p>;

  const aff = ev.affidavit || {};
  // Open the actual affidavit via the authenticated API (view token for the persisted file,
  // or an authenticated blob for legacy affidavits) — never a raw top-level navigation.
  const openAffidavit = async () => {
    const fid = aff.document_id;
    if (fid) {
      const tab = window.open("about:blank", "_blank");
      try {
        const { data } = await api.post(`/files/${fid}/view-token`);
        const url = `${API}/files/${fid}?token=${encodeURIComponent(data.token)}&inline=1`;
        if (tab) tab.location.replace(url); else window.location.assign(url);
      } catch { if (tab) tab.close(); }
      return;
    }
    try {
      const resp = await api.get(`/orders/${orderId}/affidavit/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* no-op */ }
  };
  return (
    <div data-testid="evidence-chain" className="space-y-4">
      {ev.attempts.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No attempts logged yet.</p>}
      {ev.attempts.map((a) => role === "client" ? <ClientAttemptCard key={a.attempt_number} a={a} /> : (
        <div key={a.attempt_number} data-testid={`evidence-attempt-${a.attempt_number}`} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-display text-base font-bold text-slate-900">Attempt #{a.attempt_number}</h4>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${a.outcome === "served" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {a.outcome === "served" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {(a.outcome || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          </div>
          <p className="mt-1 font-mono-arps text-[11px] text-slate-400" data-testid={`evidence-times-${a.attempt_number}`}>
            Device time: {fmt(a.device_timestamp) || fmt(a.timestamp) || "—"} · Server received: {fmt(a.server_received_at) || fmt(a.timestamp) || "—"} · {a.server_name}
          </p>
          {a.counts_toward_diligence === false && (
            <div data-testid={`evidence-unverified-${a.attempt_number}`} className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="font-semibold">{(a.verification_flags || []).includes("no_gps") ? "Unverified (no GPS)" : "Photo not live capture"}</span>
              {a.admin_override ? (
                <span className="font-semibold text-emerald-700" data-testid={`evidence-override-applied-${a.attempt_number}`}>· Admin override applied by {a.admin_override.by}</span>
              ) : role === "admin" ? (
                <button data-testid={`evidence-override-btn-${a.attempt_number}`} onClick={() => overrideAttempt(a)} className="ml-auto rounded-md border border-amber-300 bg-white px-2 py-1 font-semibold text-amber-800 hover:bg-amber-100">Override (count & pay)</button>
              ) : null}
            </div>
          )}
          {a.live_photo === false && (a.photos || []).length > 0 && (
            <p data-testid={`evidence-photo-notlive-${a.attempt_number}`} className="mt-1 text-[11px] font-semibold text-amber-700">⚑ Photo from desktop/gallery fallback — not a live capture; does not satisfy the photo requirement without an admin override.</p>
          )}
          {a.not_served_reason && (
            <p className="mt-1 text-xs text-slate-500" data-testid={`evidence-reason-${a.attempt_number}`}>Reason: <span className="font-medium text-slate-700">{(a.not_served_reason || "").replace(/_/g, " ")}</span></p>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-100 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><MapPin className="h-4 w-4 text-emerald-600" /> GPS evidence <StateBadge state={a.gps.state} testid={`evidence-gps-${a.attempt_number}`} /></div>
              {a.gps.state === "present" ? (
                <div className="space-y-0.5 font-mono-arps text-xs text-slate-600">
                  <p>Lat: {Number(a.gps.lat).toFixed(5)}</p>
                  <p>Lng: {Number(a.gps.lng).toFixed(5)}</p>
                  {a.gps.accuracy != null && <p>Accuracy: ±{Math.round(a.gps.accuracy)}m</p>}
                  <p>Recorded: {fmt(a.gps.timestamp)}</p>
                </div>
              ) : <p className="text-xs text-red-500">No GPS coordinates recorded for this attempt.</p>}
              <ProximityNote v={a.gps.validation} testid={`evidence-proximity-${a.attempt_number}`} />
              <CensusVerification cv={a.gps.census_verification} attemptNo={a.attempt_number} />
            </div>
            <div className="rounded-lg border border-slate-100 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Camera className="h-4 w-4 text-sky-600" /> Photo evidence <StateBadge state={a.photo_summary.state} testid={`evidence-photo-summary-${a.attempt_number}`} /></div>
              {a.photos.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {a.photos.map((p, i) => <PhotoTile key={i} photo={p} idx={i} attemptNo={a.attempt_number} />)}
                </div>
              ) : <p className="text-xs text-slate-400">{a.photo_summary.state === "required_missing" ? "A photo is required for this service type but none was submitted." : "No photo required for this service type."}</p>}
            </div>
          </div>
          {a.notes && <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{a.notes}</p>}
          <p className="mt-2 text-[11px] text-slate-400">Evidence submitted: {fmt(a.evidence_submitted_at)}</p>
          <SupplementalEvidenceList orderId={orderId} attemptNumber={a.attempt_number} />
        </div>
      ))}

      <div data-testid="evidence-affidavit" className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="flex items-center gap-1.5 font-display text-base font-bold text-slate-900"><FileSignature className="h-4 w-4 text-indigo-600" /> Affidavit / Proof of service</h4>
          <StateBadge state={aff.state} testid="evidence-affidavit-state" />
        </div>
        {aff.download_url ? (
          <div className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <p><span className="text-slate-500">Document type:</span> <span className="font-medium text-slate-800">{aff.document_type}</span></p>
            <p><span className="text-slate-500">Version:</span> <span className="font-medium text-slate-800">v{aff.version} of {aff.total_versions}</span></p>
            <p><span className="text-slate-500">Submitted by:</span> <span className="font-medium text-slate-800">{aff.submitted_by}</span></p>
            <p><span className="text-slate-500">Submitted:</span> <span className="font-medium text-slate-800">{fmt(aff.submitted_at)}</span></p>
            <p><span className="text-slate-500">Review status:</span> <span className="font-medium text-slate-800">{(aff.review_status || "").replace(/_/g, " ")}</span></p>
            <p><span className="text-slate-500">Approval:</span> <span className="font-medium text-slate-800">{aff.approval_status}</span></p>
            <p><span className="text-slate-500">Seal:</span> <span className="font-medium text-slate-800">{aff.sealed ? `Sealed by ${aff.sealed_by || "ARPS"}` : "Not sealed"}</span></p>
            <p><span className="text-slate-500">Integrity:</span> <span className="font-medium text-slate-800">{aff.integrity_status}</span></p>
            <button type="button" data-testid="evidence-affidavit-open" onClick={openAffidavit} className="mt-2 inline-flex items-center gap-1.5 text-left text-sm font-semibold text-indigo-600 hover:underline sm:col-span-2"><Download className="h-3.5 w-3.5" /> Open actual affidavit document</button>
          </div>
        ) : <p className="mt-2 text-sm text-slate-400">{aff.detail}</p>}
      </div>
    </div>
  );
}
